import _ from "lodash";
import { connect } from "react-redux";

import UnpActions from "../../../Stores/redux/UnPersist/Actions";
import PActions from "../../../Stores/redux/Persist/Actions";
import AppComponent from "../../../Components/AppComponent";
import api from "../../../Services/Api/api";
import AppModal from "../../../Components/Models/app-modal";

import SelectTransactionTypeStep, {
  formatTypeLabel,
} from "./FormSteps/SelectTransactionType";
import SelectAgentStep from "./FormSteps/SelectAgentStep";
import CodAmountStep from "./FormSteps/CodAmountStep";
import RTOLossStep from "./FormSteps/RTOLossStep";
import ItemCountStep from "./FormSteps/ItemCountStep";
import DateSelectStep from "./FormSteps/DateSelectStep";
import ExpenseCategoryStep from "./FormSteps/ExpenseCategoryStep";
import {
  CodPaidInByAgentStep,
  EmployeeSalaryPayoutStep,
  OfficeExpenseAmountStep,
  OwnerCollectionAmountStep,
  PaidInByAgentStep,
  PaidToAgentStep,
} from "./FormSteps/AmountDetailsStep";
import { getFullName } from "../../../Modules/utils/fullname";
import FinalSummaryAgentReport from "./FormSteps/FinalSummeryAgentReport";
import FinalSummaryUserPayIn from "./FormSteps/FinalSummaryUserPayIn";
import FinalSummaryUserPayOut from "./FormSteps/FinalSummaryUserPayOut";
import FinalSummaryOfficeExpense from "./FormSteps/FinalSummaryOfficeExpense";
import FinalSummaryOwnerCollection from "./FormSteps/FinalSummaryOwnerCollection";
import AdjustmentBalanceStep from "./FormSteps/AdjustmentBalanceStep";
import FinalSummaryBalanceAdjustment from "./FormSteps/FinalSummaryBalanceAdjustment";
import SelectOfficeEmployeeStep from "./FormSteps/SelectOfficeEmployeeStep";
import EmployeeBaseSalaryStep from "./FormSteps/EmployeeBaseSalaryStep";

const SCREEN_NAME = "RECORD_TRANSACTION_SCREEN";

class RecordTransactionInner extends AppComponent {
  constructor(props) {
    super(props);

    const t = props.transaction || {};
    const txType = props.transactionType || t.transactionType || "";

    // rtoLossList shape: [{ id, amount, note }]
    this.state = {
      step: 1,
      _id: t._id,
      transactionType: txType,
      // related user
      agent: t.relatedUser || t.agent || null,
      office: props.user,

      // amounts
      totalAmount: t.totalAmount ?? 0, // read-only (computed)
      totalAmountDetails: {
        cashAmount: t.totalAmountDetails?.cashAmount ?? "",
        onlineAmount: t.totalAmountDetails?.onlineAmount ?? "",
        cashDenominations: t.totalAmountDetails?.cashDenominations ?? null,
      },
      meta: t.meta,

      // cod specific
      cod: {
        totalCOD: t.cod?.totalCOD ?? "",
        deliveryCount: t.cod?.deliveryCount ?? "",
        pickupCount: t.cod?.pickupCount ?? "",
        fad: t.cod?.fad ?? "",
        deliveryRate: t.cod?.deliveryRate ?? 0,
        pickupRate: t.cod?.pickupRate ?? 0,
      },

      salaryDetails: {
        baseSalary: t.salaryDetails?.baseSalary,
      },

      rtoLossList: t.rtoLossList ? _.cloneDeep(t.rtoLossList) : [],

      // common
      transactionItem: t.transactionItem ?? "",
      note: t.note ?? "",
      date: t.date ? new Date(t.date) : null,
      officeBalance: t.officeBalance, // not for edit, only for info in edit mode

      loading: false,
      error: null,
      submitted: false,
    };
  }

  componentDidMount() {
    this.onMount();
    this.loadAgents();

    if (this.props.visible) {
      this.restoreDraftIfAvailable();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (!this.props.visible) return;

    if (!_.isEqual(prevState, this.state)) {
      this.saveDraft();
    }
  }

  async loadAgents() {
    try {
      const { users: agents } = await api.get("v1/office/user", {
        queryStr: JSON.stringify({
          where: { role: { $in: ["agent", "employee"] } },
          limit: 0,
        }),
      });
      this.props.setScreenState({ agents }, false, "AGENTS_SCREEN");
    } catch (e) {
      this.setAsyncState({ error: e.message });
    }
  }

  async onSubmitFinal() {
    try {
      if (this.state.loading) return;
      if (
        !window.confirm(
          "Are all values correct? You may not be able to edit the transaction after submission.",
        )
      )
        return;

      this.setState({ loading: true, error: null });

      const payload = this.buildPayload({
        isEditing: !!this.props.transaction?._id,
      });

      const res = this.props.transaction?._id
        ? await api.patch(
            `v1/office/transaction/${this.props.transaction._id}`,
            payload,
          )
        : await api.post("v1/office/transaction", payload);

      // server returns transaction in res.transaction or res.data
      const transaction = res?.transaction || res?.data || res;
      this.setState({ loading: false, submitted: true, transaction });
      this.props.onSubmit?.(transaction);

      this.clearDraft();
    } catch (err) {
      this.setState({ loading: false, error: err.message || String(err) });
    }
  }

  handleClose() {
    this.clearDraft();
    this.props.handleClose();
  }

  buildPayload({ isEditing = false }) {
    const s = this.state;

    // map agent to relatedUser id
    const relatedUser = s.agent?._id || s.agent || null;

    // common totalAmountDetails
    const totalAmountDetails = {
      cashAmount: Number(s.totalAmountDetails.cashAmount || 0),
      onlineAmount: Number(s.totalAmountDetails.onlineAmount || 0),
      cashDenominations: s.totalAmountDetails.cashDenominations,
    };

    // base payload
    const payload = {
      transactionType: s.transactionType,
      totalAmount: Number(s.totalAmount || 0),
      totalAmountDetails,
      meta: s.meta,
      transactionItem: s.transactionItem,
      note: s.note,
      date: s.date,
    };

    if (s.office) payload.office = s.office?._id || s.office;

    // attach office is handled by server (front-end not required); createdBy etc auto-filled
    // attach cod subdocument where needed

    if (
      ["agent_report", "user_payin", "user_payout", "adjustment"].includes(
        s.transactionType,
      )
    ) {
      payload.relatedUser = relatedUser;
    } else if (["owner_collection"].includes(s.transactionType)) {
      payload.relatedUser = this.props.user.headOffice;
    }

    if (s.transactionType === "agent_report") {
      payload.cod = {
        totalCOD: Number(s.cod.totalCOD || 0),
        deliveryCount: Number(s.cod.deliveryCount || 0),
        pickupCount: Number(s.cod.pickupCount || 0),
        fad: Number(s.cod.fad || 0),
        deliveryRate: Number(s.cod.deliveryRate || 0),
        pickupRate: Number(s.cod.pickupRate || 0),
      };
    } else if (s.transactionType === "office_expense") {
      payload.transactionItem =
        s.transactionItem || s.expenseCategory || "Office Expense";
    } else if (s.transactionType === "user_payin") {
      payload.rtoLossList = (s.rtoLossList || []).map((r) => ({
        id: r.id || r._id || "",
        amount: Number(r.amount || 0),
        note: r.note,
      }));
    } else if (s.transactionType === "user_payout") {
      payload.salaryDetails = {
        baseSalary: Number(s.salaryDetails?.baseSalary || 0),
      };
    }

    if (isEditing)
      return _.omit(payload, ["relatedUser", "office", "transactionSubType"]);
    return payload;
  }

  renderStep() {
    const { transactionType } = this.state;

    if (!transactionType)
      return (
        <SelectTransactionTypeStep
          data={this.state}
          onSubmit={(data) => this.setState({ ...data })}
          allowedTransactionTypes={this.props.allowedTransactionTypes}
        />
      );

    const stepsByType = {
      agent_report: [
        SelectAgentStep,
        CodAmountStep,
        ItemCountStep,
        CodPaidInByAgentStep,
        DateSelectStep,
        FinalSummaryAgentReport,
      ],
      user_payin: [
        SelectAgentStep,
        PaidInByAgentStep,
        RTOLossStep,
        DateSelectStep,
        FinalSummaryUserPayIn,
      ],
      user_payout: [
        SelectAgentStep,
        PaidToAgentStep,
        DateSelectStep,
        FinalSummaryUserPayOut,
      ],
      office_expense: [
        ExpenseCategoryStep,
        OfficeExpenseAmountStep,
        DateSelectStep,
        FinalSummaryOfficeExpense,
      ],
      owner_collection: [
        OwnerCollectionAmountStep,
        DateSelectStep,
        FinalSummaryOwnerCollection,
      ],
      adjustment: [
        SelectAgentStep,
        AdjustmentBalanceStep,
        DateSelectStep,
        FinalSummaryBalanceAdjustment,
      ],
    };

    const StepComponent = stepsByType[transactionType][this.state.step - 1];
    const totalSteps = stepsByType[transactionType].length;

    return (
      <StepComponent
        data={{
          ...this.state,
          ...(this.state.transaction && this.state.submitted
            ? this.state.transaction
            : {}),
        }}
        loadAgents={this.loadAgents.bind(this)}
        agents={this.props.agents}
        totalSteps={totalSteps}
        onSubmit={(data) => this.setAsyncState({ ...data })}
        onFinalSubmit={() => this.onSubmitFinal()}
        handleClose={this.handleClose.bind(this)}
        isEditing={!!this.props.transaction?._id}
        user={this.props.user}
      />
    );
  }

  getModalTitle() {
    const part1 = this.props.transaction?._id
      ? "Edit Transaction"
      : this.props.transaction?.transactionType
        ? `Record ${formatTypeLabel(
            this.props.transaction?.transactionType,
            this.props.allowedTransactionTypes,
          )}`
        : "Record Transaction";

    const part2 = this.state.agent?._id
      ? " - " + getFullName(this.state.agent)
      : "";

    return (
      <>
        {part1}
        <span style={{ fontStyle: "italic", fontWeight: "normal" }}>
          {part2}
        </span>
      </>
    );
  }

  getDraftIdentityKey() {
    const userId = this.props.user?._id || "no-user";
    const officeId =
      this.state.office?._id ||
      this.state.office ||
      this.props.user?.office ||
      "no-office";

    return `${userId}:${officeId}`;
  }

  saveDraft() {
    if (this.props.transaction?._id) return; // editing
    if (this.state.submitted) return;
    if (!this.props.visible) return;

    const draftPayload = {
      key: this.getDraftIdentityKey(),
      data: { ...this.state },
    };

    this.props.setScreenState(
      { transactionDraft: draftPayload },
      true, // persist
      SCREEN_NAME,
    );
  }

  restoreDraftIfAvailable() {
    if (this.props.transaction?._id) return; // editing mode

    const saved = this.props.transactionDraft;

    if (!saved?.data || !saved?.key) return;

    const currentKey = this.getDraftIdentityKey();

    if (saved.key !== currentKey) return; // KEY MISMATCH → DO NOT RESTORE

    if (
      window.confirm(
        "You have an unfinished transaction. Do you want to restore it?",
      )
    ) {
      this.setState({ ...saved.data });
    } else {
      this.clearDraft();
    }
  }

  clearDraft() {
    this.props.setScreenState({ transactionDraft: null }, true, SCREEN_NAME);
  }

  render() {
    return (
      <AppModal
        visible={this.props.visible}
        handleClose={this.handleClose.bind(this)}
        title={this.getModalTitle()}
        showFooter={false}
        // modalProps={{ centered: true }}
      >
        {this.renderStep()}
      </AppModal>
    );
  }
}

class RecordTransactionHeadOfficeInner extends RecordTransactionInner {
  renderStep() {
    const { transactionType } = this.state;

    if (!transactionType)
      return (
        <SelectTransactionTypeStep
          data={this.state}
          onSubmit={(data) => this.setState({ ...data })}
          allowedTransactionTypes={this.props.allowedTransactionTypes}
        />
      );

    const stepsByType = {
      user_payout: [
        SelectOfficeEmployeeStep,
        EmployeeBaseSalaryStep,
        EmployeeSalaryPayoutStep,
        DateSelectStep,
        FinalSummaryUserPayOut,
      ],
      user_payin: [
        SelectOfficeEmployeeStep,
        PaidInByAgentStep,
        DateSelectStep,
        FinalSummaryUserPayIn,
      ],
      office_expense: [
        ExpenseCategoryStep,
        OfficeExpenseAmountStep,
        DateSelectStep,
        FinalSummaryOfficeExpense,
      ],
    };

    const StepComponent = stepsByType[transactionType][this.state.step - 1];
    const totalSteps = stepsByType[transactionType].length;

    return (
      <StepComponent
        data={{
          ...this.state,
          ...(this.state.transaction && this.state.submitted
            ? this.state.transaction
            : {}),
        }}
        loadAgents={this.loadAgents.bind(this)}
        agents={this.props.agents}
        totalSteps={totalSteps}
        onSubmit={(data) => this.setAsyncState({ ...data })}
        onFinalSubmit={() => this.onSubmitFinal()}
        handleClose={this.handleClose.bind(this)}
        isEditing={!!this.props.transaction?._id}
        user={this.props.user}
      />
    );
  }
}

const mapStateToProps = (state) => ({
  agents: state.vState.AGENTS_SCREEN?.agents,
  user: state.pState.user.user,
  transactionDraft: state.pState[SCREEN_NAME]?.transactionDraft,
});

const mapDispatchToProps = (dispatch) => ({
  setScreenState: (obj, persist = false, screenName = SCREEN_NAME) =>
    persist
      ? dispatch(PActions.setPScreenState(screenName, obj))
      : dispatch(UnpActions.setVScreenState(screenName, obj)),
});

const RecordTransaction = connect(
  mapStateToProps,
  mapDispatchToProps,
)(RecordTransactionInner);

export const RecordTransactionHeadOffice = connect(
  mapStateToProps,
  mapDispatchToProps,
)(RecordTransactionHeadOfficeInner);

export default RecordTransaction;
